import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useDashboard } from '@/context/DashboardContext';
import { useCopy, useCopyResolver } from '@/context/CopyContext';
import { MembershipBadge, TierBadge } from '@/components/dashboard/MembershipBadge';
import { useAccess } from '@/components/dashboard/access';
import { LifetimeUpgradeModal } from '@/components/dashboard/LifetimeUpgrade';
import LegalGate from '@/components/dashboard/LegalGate';
import DashLangSwitch, { useDashLangSync } from '@/components/dashboard/DashLangSwitch';
import { useSiteLang } from '@/components/site/SiteLang';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, ChartColumn, TrendingUp, Building2, GraduationCap, Briefcase, Megaphone, Users, Receipt, User, ShieldCheck, LogOut, Menu, X, ChevronRight, MessageSquare, Calculator, FileText, CalendarDays, Lock } from 'lucide-react';

// Nav items reference copy keys instead of hardcoded labels — labels are
// resolved inside the component via useCopy(). Keeps strings editable from
// the admin Copy Editor without changing this file.
// `feature` = clave de la matriz de acceso (components/dashboard/access.js). Lo que el nivel del usuario
// no permite no aparece en el menú; Oportunidades es la excepción (`showLocked`): free la ve con candado.
const NAV_ITEMS = [
  { path: '/dashboard', labelKey: 'nav.inicio', group: 'negocio', icon: LayoutDashboard, feature: 'home' },
  // Oportunidades: visible para todos; sin membresía se ve «censurada» (decisión 08/10/2026)
  { path: '/dashboard/propiedades', labelKey: 'nav.propiedades', group: 'negocio', icon: Building2, feature: 'opportunities', showLocked: true },
  // Mis operaciones (16/10/2026): lo aportado por el socio a las operaciones del club
  { path: '/dashboard/operaciones', labelKey: 'nav.operaciones', group: 'negocio', icon: TrendingUp, feature: 'operations' },
  { path: '/dashboard/actividad', labelKey: 'nav.actividad', group: 'negocio', icon: ChartColumn, feature: 'sales' },
  // Comisiones incluye el resumen (antes «Rendimiento») y el pipeline de contactos (antes «Mis contactos»)
  { path: '/dashboard/comisiones', labelKey: 'nav.comisiones', group: 'negocio', icon: Receipt, feature: 'commissions' },
  { path: '/dashboard/afiliados', labelKey: 'nav.afiliados', group: 'red', icon: Users, feature: 'network' },
  { path: '/dashboard/academia', labelKey: 'nav.academia', group: 'herr', icon: GraduationCap, feature: 'academy' },
  { path: '/dashboard/eventos', labelKey: 'nav.eventos', group: 'herr', icon: CalendarDays, feature: 'events' },
  { path: '/dashboard/chat', labelKey: 'nav.chat', group: 'herr', icon: MessageSquare, feature: 'chat' },
  { path: '/dashboard/success-kit', labelKey: 'nav.success_kit', group: 'herr', icon: Briefcase, feature: 'successKit' },
  { path: '/dashboard/marketing', labelKey: 'nav.marketing', group: 'herr', icon: Megaphone, feature: 'marketing' },
  { path: '/dashboard/calculadora', labelKey: 'nav.calculadora', group: 'herr', icon: Calculator, feature: 'calculator' },
  { path: '/dashboard/documentos', labelKey: 'nav.documentos', group: 'cuenta', icon: FileText, feature: 'documents' },
  { path: '/dashboard/perfil', labelKey: 'nav.perfil', group: 'cuenta', icon: User, feature: 'profile' },
];

const ADMIN_ITEM = { path: '/dashboard/admin', labelKey: 'nav.admin', group: 'admin', icon: ShieldCheck };

// Agrupación del menú (evita una lista larga con scroll). Orden de aparición.
const NAV_GROUPS = [
  { id: 'negocio', label: { es: 'Mi negocio', en: 'My business' } },
  { id: 'red', label: { es: 'Mi red', en: 'My network' } },
  { id: 'herr', label: { es: 'Herramientas', en: 'Tools' } },
  { id: 'cuenta', label: { es: 'Mi cuenta', en: 'My account' } },
  { id: 'admin', label: { es: 'Administración', en: 'Admin' } },
];

// Textos fijos del layout (los del menú y la cabecera vienen de copyRegistry vía useCopy)
const T = {
  es: {
    membersOnly: 'Solo miembros',
    logoAlt: 'Freedom Homes Club — inicio',
    navLabel: 'Navegación del panel',
    menu: 'Menú',
    closeMenu: 'Cerrar menú',
    openMenu: 'Abrir menú',
  },
  en: {
    membersOnly: 'Members only',
    logoAlt: 'Freedom Homes Club — home',
    navLabel: 'Dashboard navigation',
    menu: 'Menu',
    closeMenu: 'Close menu',
    openMenu: 'Open menu',
  },
};

// Path → copy key for the breadcrumb header. Labels are resolved at render time.
const PAGE_TITLE_KEYS = {
  '/dashboard': 'nav.inicio',
  '/dashboard/actividad': 'nav.actividad',
  '/dashboard/operaciones': 'nav.operaciones',
  '/dashboard/propiedades': 'nav.propiedades',
  '/dashboard/academia': 'nav.academia',
  '/dashboard/success-kit': 'nav.success_kit',
  '/dashboard/marketing': 'nav.marketing',
  '/dashboard/afiliados': 'nav.afiliados',
  '/dashboard/comisiones': 'nav.comisiones',
  '/dashboard/chat': 'nav.chat',
  '/dashboard/calculadora': 'nav.calculadora',
  '/dashboard/eventos': 'nav.eventos',
  '/dashboard/documentos': 'nav.documentos',
  '/dashboard/perfil': 'nav.perfil',
  '/dashboard/admin': 'nav.admin',
};

export default function DashboardLayout() {
  const { user, logout, isAdmin, membership } = useDashboard();
  const activeMembership = membership?.active_membership;
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Menú según el nivel real (free / Access / PRO / Premium / equipo). Mientras carga la membresía
  // se muestra lo de free (nunca de más).
  const access = useAccess();
  const visibleItems = NAV_ITEMS.filter((item) => access.can(item.feature) || item.showLocked);
  const items = isAdmin ? [...visibleItems, ADMIN_ITEM] : visibleItems;

  // Resolved copy strings
  const t = useCopyResolver();
  const breadcrumbRoot = useCopy('layout.breadcrumb_root');
  useDashLangSync();
  const { lang } = useSiteLang();
  const L = T[lang] || T.es;
  const greeting = useCopy('layout.greeting');
  const roleAdminLabel = useCopy('layout.role_admin');
  const logoutLabel = useCopy('layout.logout');

  const currentPageKey = PAGE_TITLE_KEYS[location.pathname];
  const currentPage = currentPageKey
    ? t(currentPageKey)
    : (location.pathname.startsWith('/dashboard/propiedades/') ? t('nav.propiedades')
      : location.pathname.startsWith('/dashboard/academia/') ? t('nav.academia')
        : location.pathname.startsWith('/dashboard/documentos/') ? t('nav.documentos') : breadcrumbRoot);

  // Título de la pestaña del navegador por sección (accesibilidad y orientación)
  useEffect(() => {
    document.title = currentPage ? `${currentPage} · Freedom Homes Club` : 'Freedom Homes Club';
  }, [currentPage]);

  // Menú móvil: se cierra con Esc y al cambiar de página
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const renderItem = (item) => {
      const active = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
      const isExact = item.path === '/dashboard' && location.pathname === '/dashboard';
      const isActive = isExact || (item.path !== '/dashboard' && active);
      return (
        <Link
          key={item.path}
          to={item.path}
          onClick={() => setSidebarOpen(false)}
          className={`group relative flex items-center gap-3 px-3 py-1.5 rounded-xl text-sm transition-all duration-200 ${isActive
            ? 'text-[#c9a84c]'
            : 'text-gray-400 hover:text-gray-100'
          }`}
          data-testid={`nav-${item.labelKey.replace(/[^a-z0-9]+/gi, '-')}`}
        >
          {isActive && (
            <motion.div
              layoutId="sidebar-active"
              className="absolute inset-0 rounded-xl bg-[#c9a84c]/[0.08] border border-[#c9a84c]/20"
              transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            />
          )}
          {!isActive && (
            <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/[0.03] transition-colors duration-200" />
          )}
          <span className="relative z-10 flex items-center gap-3">
            <item.icon size={17} strokeWidth={isActive ? 2.2 : 1.6} />
            <span className={isActive ? 'font-medium' : ''}>{t(item.labelKey)}</span>
            {item.showLocked && !access.can(item.feature) && <Lock size={11} className="text-[#c9a84c]/70" aria-label={L.membersOnly} />}
          </span>
          {isActive && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-[#c9a84c]"
            />
          )}
        </Link>
      );
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-4 border-b border-white/[0.06]">
        <Link to="/dashboard" className="flex items-center gap-3">
          <img src="/logo-fhc.png" alt={L.logoAlt} className="h-9 w-auto" />
        </Link>
      </div>

      <nav className="flex-1 py-3 px-3 overflow-y-auto" aria-label={L.navLabel}>
        {NAV_GROUPS.map(g => {
          const groupItems = items.filter(i => i.group === g.id);
          if (!groupItems.length) return null;
          return (
            <div key={g.id} className={`mb-1.5 ${g.id === 'admin' ? 'mt-3 pt-3 border-t border-white/[0.06]' : ''}`}>
              <p className="px-3 pt-0.5 pb-0.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-gray-400">{g.label[lang] || g.label.es}</p>
              <div className="space-y-0.5">{groupItems.map(renderItem)}</div>
            </div>
          );
        })}
      </nav>

      <div className="p-4 mx-3 mb-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
        <div className="flex items-center gap-3 mb-3">
          <div className="relative">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#c9a84c] to-[#a08339] flex items-center justify-center text-[#0a1628] text-sm font-bold shadow-lg shadow-[#c9a84c]/10">
              {user?.name?.charAt(0)}
            </div>
            {activeMembership && (
              <div className="absolute -bottom-0.5 -right-0.5 ring-2 ring-[#0a1225]">
                <MembershipBadge membership={activeMembership} variant="inline" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm text-white font-medium truncate">{user?.name}</p>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-[11px] text-[#c9a84c]/70 uppercase tracking-wider font-medium">{user?.level}</p>
              {activeMembership && <MembershipBadge membership={activeMembership} variant="compact" />}
            </div>
          </div>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-400 hover:text-red-400 rounded-lg hover:bg-red-500/[0.06] transition-all duration-200"
          data-testid="dashboard-logout"
        >
          <LogOut size={14} /> {logoutLabel}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#060d1b] flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-[260px] flex-col fixed inset-y-0 left-0 bg-[#0a1225]/95 backdrop-blur-2xl border-r border-white/[0.06] z-30">
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed inset-y-0 left-0 w-[260px] bg-[#0a1225]/98 backdrop-blur-2xl border-r border-white/[0.06] z-50 lg:hidden"
            role="dialog" aria-modal="true" aria-label={L.menu}
          >
            <button onClick={() => setSidebarOpen(false)} aria-label={L.closeMenu}
              className="absolute top-4 right-3 z-10 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.06]">
              <X size={18} />
            </button>
            <SidebarContent />
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex-1 min-w-0 lg:ml-[260px] flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 bg-[#060d1b]/80 backdrop-blur-2xl border-b border-white/[0.06] px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-400 hover:text-white transition-colors" aria-label={L.openMenu} data-testid="mobile-menu-toggle">
              <Menu size={20} />
            </button>
            <div className="hidden sm:flex items-center gap-2 text-sm">
              <span className="text-gray-400">{breadcrumbRoot}</span>
              <ChevronRight size={12} className="text-gray-400" aria-hidden="true" />
              <span className="text-white font-medium">{currentPage}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <DashLangSwitch />
            <span className="hidden sm:inline-flex items-center gap-2 text-xs text-gray-400">
              {greeting} <span className="text-gray-300">{user?.name?.split(' ')[0]}</span>
            </span>
            {/* Nivel real del usuario (antes decía «Broker» a todo el mundo) */}
            {(access.ready || access.isStaff) && (
              <TierBadge tier={access.tier} label={access.tier === 'admin' ? roleAdminLabel : undefined} />
            )}
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <LegalGate><Outlet /></LegalGate>
          </motion.div>
        </main>
        <LifetimeUpgradeModal />
      </div>
    </div>
  );
}
